 
  import { useState } from "react";
import {
  Box, Typography, Paper, Grid,
  Button, Table, TableHead, TableRow,
  TableCell, TableBody, CircularProgress,
  Alert, TextField, Collapse, IconButton, Chip
} from "@mui/material";
import ExpandMoreIcon           from "@mui/icons-material/ExpandMore";
import ExpandLessIcon           from "@mui/icons-material/ExpandLess";
import TwoWheelerIcon           from "@mui/icons-material/TwoWheeler";
import ElectricRickshawIcon     from "@mui/icons-material/ElectricRickshaw";


import { salesReportService } from "../service/SalesReportService";

import api                 from "../service/api";                   // adjust path

// ── adjust these to your actual service methods ───────────────────────────────
 import EvStockDetailDialog from "../component/EvStockDetailDialog";
// evSalesReportService.getByDate(date)       → GET /ev-sold-date/:date
// evSalesReportService.getWeekly(year)       → GET /ev-weekly/:year
// evSalesReportService.getMonthly(year,month)→ GET /ev-monthly/:year/:month

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt  = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtR = (n) => `₹${fmt(n)}`;

const SALE_TYPE_STYLE = {
  CASH:      { bg: "#e8f5e9", color: "#2e7d32" },
  FINANCE:   { bg: "#e3f2fd", color: "#1565c0" },
  EXCHANGE:  { bg: "#fff3e0", color: "#e65100" },
  "EX+FIN":  { bg: "#ede7f6", color: "#4527a0" },
  "CASH+EX": { bg: "#fce4ec", color: "#ad1457" },
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// ─────────────────────────────────────────────────────────────────────────────
const SalesReport = () => {

  const [mode,          setMode]          = useState("monthly");
  const [date,          setDate]          = useState("");
  const [year,          setYear]          = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);

  const [tableData, setTableData] = useState({});   // { location: [sales] }
  const [weekList,  setWeekList]  = useState([]);
  const [summary,   setSummary]   = useState(null);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  // collapse state per location — default all open
  const [collapsed, setCollapsed] = useState({});
  const toggleLocation = (loc) =>
    setCollapsed((p) => ({ ...p, [loc]: !p[loc] }));

  // view dialog
  const [viewOpen,    setViewOpen]    = useState(false);
  const [viewData,    setViewData]    = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError,   setViewError]   = useState("");

  // ── Group sales by location ──────────────────────────────────────────────
  const groupByLocation = (sales = []) => {
    const grouped = {};
    sales.forEach((s) => {
      const loc = s.location || "Other";
      if (!grouped[loc]) grouped[loc] = [];
      grouped[loc].push(s);
    });
    setCollapsed({});   // reset — all open on new data
    return grouped;
  };

  // ── DAILY ────────────────────────────────────────────────────────────────
  const handleDaily = async () => {
    try {
      setLoading(true); setError("");
      const res = await salesReportService.getByDate(date);
      const sales = [
        ...(res.data?.cash?.sales     || []),
        ...(res.data?.finance?.sales  || []),
        ...(res.data?.exchange?.sales || []),
      ];
      setTableData(groupByLocation(sales));
      setSummary(res.summary || null);
      setWeekList([]);
    } catch { setError("Daily data failed"); }
    finally  { setLoading(false); }
  };

  // ── WEEKLY ───────────────────────────────────────────────────────────────
  const handleWeekly = async () => {
    try {
      setLoading(true); setError("");
      const res = await salesReportService.getWeekly(year);

      // Merge cash + finance + exchange weeks keyed by week number
      const weekMap = {};
      [...(res.data?.cash || []), ...(res.data?.finance || []), ...(res.data?.exchange || [])].forEach((w) => {
        if (!weekMap[w.week]) {
          weekMap[w.week] = { week: w.week, totalSold: 0, totalRevenue: 0, totalDue: 0, sales: [] };
        }
        weekMap[w.week].totalSold    += w.totalSold    || 0;
        weekMap[w.week].totalRevenue += w.totalRevenue || 0;
        weekMap[w.week].totalDue     += w.totalDue     || 0;
        weekMap[w.week].sales         = [...weekMap[w.week].sales, ...(w.sales || [])];
      });

      setWeekList(Object.values(weekMap).sort((a, b) => a.week - b.week));
      setSummary(res.summary || null);
      setTableData({});
    } catch { setError("Weekly data failed"); }
    finally  { setLoading(false); }
  };

  const handleWeekClick = (week) => {
    setTableData(groupByLocation(week.sales || []));
  };

  // ── MONTHLY ──────────────────────────────────────────────────────────────
  const handleMonthClick = async (month) => {
    try {
      setLoading(true); setError("");
      setSelectedMonth(month);
      const res = await salesReportService.getMonthly(year, month);
      const sales = [
        ...(res.data?.cash?.sales     || []),
        ...(res.data?.finance?.sales  || []),
        ...(res.data?.exchange?.sales || []),
      ];
      setTableData(groupByLocation(sales));
      setSummary(res.summary || null);
      setWeekList([]);
    } catch { setError("Monthly data failed"); }
    finally  { setLoading(false); }
  };

  // ── VIEW — fetch stock detail by evStockId ────────────────────────────────
  const handleView = async (sale) => {
    try {
      setViewOpen(true);
      setViewLoading(true);
      setViewError("");
      setViewData(null);
      // sale._id is the sale id; adjust endpoint to your actual route
      const res = await api.get(`/specific-sold-bike-details/${sale._id}`);
      setViewData(res.data?.data || res.data);

      console.log(res.data.data,"mdmojoowko")
    } catch {
      setViewError("Failed to load details");
    } finally {
      setViewLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", pl: 4  }}>

      {/* ── PAGE HEADER ── */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
        <Box sx={{ bgcolor: "#1b5e20", borderRadius: 2, p: 1, display: "flex" }}>
          <ElectricRickshawIcon sx={{ color: "#fff", fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#0d1b2a">
            EV Sales Report
          </Typography>
          <Typography variant="caption" color="text.secondary">
            E-Rickshaw & Scooty — Daily / Weekly / Monthly
          </Typography>
        </Box>
      </Box>

      {/* ── MODE SWITCH ── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button variant={mode === "daily"   ? "contained" : "outlined"}
              onClick={() => setMode("daily")}>Daily</Button>
          </Grid>
          <Grid item>
            <Button variant={mode === "weekly"  ? "contained" : "outlined"}
              onClick={() => setMode("weekly")}>Weekly</Button>
          </Grid>
          <Grid item>
            <Button variant={mode === "monthly" ? "contained" : "outlined"}
              onClick={() => setMode("monthly")}>Monthly</Button>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField label="Year" size="small" fullWidth value={year}
              onChange={(e) => setYear(e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      {/* ── DAILY UI ── */}
      {mode === "daily" && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField type="date" fullWidth size="small" label="Select Date"
                InputLabelProps={{ shrink: true }}
                value={date} onChange={(e) => setDate(e.target.value)} />
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={handleDaily} disabled={!date}>Load</Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* ── WEEKLY UI ── */}
      {mode === "weekly" && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Button variant="contained" onClick={handleWeekly}>Load Weekly</Button>
          {weekList.length > 0 && (
            <Grid container spacing={1} mt={1}>
              {weekList.map((w) => (
                <Grid item key={w.week} xs={6} sm={4} md={3}>
                  <Button fullWidth variant="outlined" onClick={() => handleWeekClick(w)}
                    sx={{ display: "flex", flexDirection: "column", py: 1, textTransform: "none" }}>
                    <Typography fontWeight={700} fontSize="0.85rem">Week {w.week}</Typography>
                    <Typography fontSize="0.7rem" color="text.secondary">
                      {w.totalSold} sales · {fmtR(w.totalRevenue)}
                    </Typography>
                  </Button>
                </Grid>
              ))}
            </Grid>
          )}
        </Paper>
      )}

      {/* ── MONTHLY UI ── */}
      {mode === "monthly" && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={1}>
            {MONTHS.map((m, i) => (
              <Grid item xs={4} sm={3} md={2} key={i}>
                <Button fullWidth
                  variant={selectedMonth === i + 1 ? "contained" : "outlined"}
                  onClick={() => handleMonthClick(i + 1)}
                  sx={{ textTransform: "none", fontWeight: 700 }}>
                  {m}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {loading && <Box py={4} textAlign="center"><CircularProgress /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── SUMMARY BAR ── */}
      {summary && !loading && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: "#1b5e20" }}>
          <Grid container spacing={2}>
            {[
              { label: "Total Sold",    value: String(summary.totalSales ?? summary.totalSold ?? 0) },
              { label: "Total Revenue", value: fmtR(summary.totalRevenue) },
              { label: "Total Due",     value: fmtR(summary.totalDue) },
              { label: "Cash",          value: `${summary.cash?.count ?? 0} · ${fmtR(summary.cash?.revenue)}` },
              { label: "Finance",       value: `${summary.finance?.count ?? 0} · ${fmtR(summary.finance?.revenue)}` },
              { label: "Exchange",      value: `${summary.exchange?.count ?? 0} · ${fmtR(summary.exchange?.revenue)}` },
            ].map(({ label, value }) => (
              <Grid item xs={6} sm={4} md={2} key={label}>
                <Typography variant="caption" color="rgba(255,255,255,0.7)"
                  sx={{ fontSize: "0.63rem", textTransform: "uppercase", letterSpacing: 0.8, display: "block" }}>
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={800} color="#fff">{value}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* ── EXPAND / COLLAPSE ALL ── */}
      {Object.keys(tableData).length > 0 && (
        <Box display="flex" gap={1} mb={1.5}>
          <Button size="small" variant="outlined"
            onClick={() => setCollapsed(Object.fromEntries(Object.keys(tableData).map((k) => [k, false])))}>
            Expand All
          </Button>
          <Button size="small" variant="outlined"
            onClick={() => setCollapsed(Object.fromEntries(Object.keys(tableData).map((k) => [k, true])))}>
            Collapse All
          </Button>
        </Box>
      )}

      {/* ── LOCATION WISE COLLAPSIBLE TABLES ── */}
      {Object.keys(tableData).map((loc) => {
        const isOpen = !collapsed[loc];
        const sales  = tableData[loc];
        const locRev = sales.reduce((s, x) => s + (x.salePrice || 0), 0);
        const dueCnt = sales.filter((s) => s.dueStatus === "DUE").length;

        return (
          <Paper sx={{ mt: 2, overflow: "hidden", borderRadius: 2 }} key={loc}>

            {/* Location Header */}
            <Box onClick={() => toggleLocation(loc)} sx={{
              px: 2, py: 1.5, cursor: "pointer",
              bgcolor: "#e8f5e9",
              borderBottom: isOpen ? "1px solid #c8e6c9" : "none",
              display: "flex", alignItems: "center", justifyContent: "space-between"
            }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography fontWeight={700} color="#1b5e20">📍 {loc}</Typography>
                <Chip size="small" label={`${sales.length} sales`}
                  sx={{ bgcolor: "#1b5e20", color: "#fff", fontWeight: 700, fontSize: "0.7rem" }} />
                {dueCnt > 0 && (
                  <Chip size="small" label={`${dueCnt} DUE`}
                    sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 700,
                      fontSize: "0.7rem", border: "1px solid #ffb74d" }} />
                )}
                <Chip size="small" label={fmtR(locRev)}
                  sx={{ bgcolor: "#f1f8e9", color: "#2e7d32", fontWeight: 700, fontSize: "0.7rem" }} />
              </Box>
              <IconButton size="small">
                {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              </IconButton>
            </Box>

            {/* Table */}
            <Collapse in={isOpen}>
              <Box sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f1f8e9" }}>
                      {["#", "Invoice", "Type", "Model", "Customer", "Mobile",
                        "Sale Price", "Sale Type", "Due", "Date", "Action"].map((h) => (
                        <TableCell key={h} sx={{
                          fontWeight: 800, fontSize: "0.72rem",
                          color: "#1b5e20", whiteSpace: "nowrap", py: 1.2
                        }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sales.map((s, idx) => {
                      const isDue   = s.dueStatus === "DUE";
                      const stStyle = SALE_TYPE_STYLE[s.saleType] || { bg: "#f5f5f5", color: "#555" };
                      return (
                        <TableRow key={s._id} sx={{
                          bgcolor: isDue ? "#fff8f5" : idx % 2 === 0 ? "#fff" : "#fafbff",
                          "&:hover": { bgcolor: isDue ? "#fff0eb" : "#f0f8f0" }
                        }}>
                          <TableCell sx={{ color: "#aaa", fontSize: "0.73rem" }}>{idx + 1}</TableCell>

                          <TableCell sx={{ fontWeight: 700, fontSize: "0.73rem", color: "#1565c0", whiteSpace: "nowrap" }}>
                            {s.invoiceNo || "—"}
                          </TableCell>

                          <TableCell>
                            <Chip size="small"
                              icon={s.vehicleType === "E_RICKSHAW"
                                ? <ElectricRickshawIcon sx={{ fontSize: 12 }} />
                                : <TwoWheelerIcon       sx={{ fontSize: 12 }} />}
                              label={s.vehicleType === "E_RICKSHAW" ? "E-Rickshaw" : "Scooty"}
                              sx={{
                                fontSize: "0.62rem", fontWeight: 700,
                                bgcolor: s.vehicleType === "E_RICKSHAW" ? "#e8f5e9" : "#e3f2fd",
                                color:   s.vehicleType === "E_RICKSHAW" ? "#2e7d32" : "#1565c0"
                              }} />
                          </TableCell>

                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                            {s.model}
                            {s.companyName && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {s.companyName}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Typography variant="body2" fontWeight={700}>{s.customerName}</Typography>
                            {s.fatherName && (
                              <Typography variant="caption" color="text.secondary">S/O {s.fatherName}</Typography>
                            )}
                          </TableCell>

                          <TableCell sx={{ fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                            {s.mobile}
                          </TableCell>

                          <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                            {fmtR(s.salePrice)}
                          </TableCell>

                          <TableCell>
                            <Chip size="small" label={s.saleType || "—"}
                              sx={{ fontSize: "0.65rem", fontWeight: 700,
                                bgcolor: stStyle.bg, color: stStyle.color }} />
                          </TableCell>

                          <TableCell>
                            <Chip size="small" label={s.dueStatus || "PAID"}
                              sx={{
                                fontSize: "0.65rem", fontWeight: 700,
                                bgcolor: isDue ? "#fff3e0" : "#e8f5e9",
                                color:   isDue ? "#e65100" : "#2e7d32",
                                border:  `1px solid ${isDue ? "#ffb74d" : "#a5d6a7"}`
                              }} />
                          </TableCell>

                          <TableCell sx={{ fontSize: "0.75rem", whiteSpace: "nowrap", color: "#666" }}>
                            {s.saleDate ? new Date(s.saleDate).toLocaleDateString("en-IN") : "—"}
                          </TableCell>

                          <TableCell>
                            <Button size="small" variant="outlined"
                              onClick={() => handleView(s)}
                              sx={{ fontSize: "0.7rem", fontWeight: 700,
                                borderRadius: 1.5, px: 1.2, py: 0.4, minWidth: 0 }}>
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>

          </Paper>
        );
      })}

      {/* ── EMPTY STATE ── */}
      {!loading && Object.keys(tableData).length === 0 && !error && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: "center", borderRadius: 2, borderStyle: "dashed", mt: 2 }}>
          <ElectricRickshawIcon sx={{ fontSize: 48, color: "#bdbdbd", mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No data loaded</Typography>
          <Typography variant="body2" color="text.secondary">
            Select a date, week, or month above to load sales
          </Typography>
        </Paper>
      )}

      {/* ── VIEW DIALOG ── */}
      <EvStockDetailDialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        data={viewData}
        loading={viewLoading}
        error={viewError}
      />

    </Box>
  );
};

export default SalesReport;